import React, { useState } from 'react'
import Select from 'react-select'
import styled, { keyframes } from 'styled-components'

import { ButtonColor, AllNotificationsContainer } from '../components/structure/styles'
import NotificationCard from '../components/structure/notifications/NotificationCard'
import useAdminNotifications from '../hooks/useAdminNotifications'

const NOTIFICATIONS_LIMIT = 50

const Notifications = () => {
  const {
    notifications,
    isLoading: notificationsLoading,
    isFetching,
    dismissedNotifications,
    dismissNotification,
    showNotificationsAgain,
    fetchNextPage,
  } = useAdminNotifications({ limit: NOTIFICATIONS_LIMIT })

  const [selectedSeverity, setSelectedSeverity] = useState<string | null>(null)
  const [selectedService, setSelectedService] = useState<string | null>(null)

  const filteredNotifications = notifications.filter(
    (notif) =>
      !dismissedNotifications.has(notif._id) &&
      (!selectedSeverity || notif.severity === selectedSeverity) &&
      (!selectedService || notif.service === selectedService)
  )

  const getUniqueSeverities = (): { value: string | null; label: string }[] => {
    const uniqueSeverities = [...new Set(notifications.map((n) => n.severity))]
    return [{ value: null, label: 'All severities' }, ...uniqueSeverities.map((s) => ({ value: s, label: s }))]
  }

  const getUniqueServices = (): { value: string | null; label: string }[] => {
    const uniqueServices = [...new Set(notifications.map((n) => n.service))]
    return [{ value: null, label: 'All services' }, ...uniqueServices.map((s) => ({ value: s, label: s }))]
  }

  return (
    <Wrap>

      <Toolbar>
        <StyledSelect
          classNamePrefix="Select"
          options={getUniqueSeverities()}
          onChange={(option: any) => setSelectedSeverity(option?.value || null)}
          isClearable
          placeholder="Filter by severity"
        />
        <StyledSelect
          classNamePrefix="Select"
          options={getUniqueServices()}
          onChange={(option: any) => setSelectedService(option?.value || null)}
          isClearable
          placeholder="Filter by service"
        />
      </Toolbar>

      {notificationsLoading ? (
        <LoaderBar />
      ) : (
        <AllNotificationsContainer>
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification) => (
              <NotificationCard key={notification._id} notification={notification} onDismiss={dismissNotification} />
            ))
          ) : (
            <ButtonColor onClick={showNotificationsAgain}>Show dismissed notifications</ButtonColor>
          )}
          {notifications.length % NOTIFICATIONS_LIMIT === 0 && (
            <ButtonColor disabled={isFetching} onClick={() => fetchNextPage()}>
              {isFetching ? 'Loading…' : 'Load more notifications'}
            </ButtonColor>
          )}
        </AllNotificationsContainer>
      )}
    </Wrap>
  )
}

export default Notifications

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111, 92, 230, 0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const StyledSelect = styled(Select)`
  width: 220px;

  .Select__control {
    background: #111;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    min-height: 34px;
    box-shadow: none;
    cursor: pointer;
    &:hover { border-color: rgba(255, 255, 255, 0.15); }
    &.Select__control--is-focused { border-color: rgba(111, 92, 230, 0.5); }
  }

  .Select__value-container { padding: 0 10px; }
  .Select__placeholder { color: #a1a1a1; font-size: 13px; }
  .Select__input-container { color: #ededed; font-size: 13px; }
  .Select__single-value { color: #ededed; font-size: 13px; }
  .Select__indicator-separator { background: rgba(255, 255, 255, 0.08); }
  .Select__dropdown-indicator { color: #777; &:hover { color: #a0a0a0; } }
  .Select__clear-indicator { color: #777; &:hover { color: #ff4444; } }

  .Select__menu {
    background: #161616;
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    z-index: 10;
  }
  .Select__option {
    background: transparent;
    color: #a0a0a0;
    font-size: 13px;
    cursor: pointer;
    &:hover, &.Select__option--is-focused { background: rgba(255, 255, 255, 0.04); color: #ededed; }
    &.Select__option--is-selected { background: rgba(111, 92, 230, 0.15); color: #a89aff; }
  }
`
